import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import {
    FormElementStyle,
    LookupFieldResult,
    LookupObject,
    LookupSourceType,
    QueryExpression,
    RandomStringPipe,
    SystemEntity,
    UTILS_ICON_DELETE,
    UTILS_ICON_SEARCH,
} from '@wdx/shared/utils';
import { WdxIsLoadingModule } from '@wdx/shared/components/wdx-is-loading';
import { WdxFormFieldModule } from '@wdx/shared/components/wdx-form-field';
import { WdxInputGroupModule } from '@wdx/shared/components/wdx-input-group';
import { MoleculeInfoCardModule } from '../../../../components/molecules/molecule-info-card/molecule-info-card.module';
import { WdxIconModule } from '@wdx/shared/components/wdx-icon';
import {
    WdxButtonModule,
    WdxButtonSize,
    WdxButtonStyle,
} from '@wdx/shared/components/wdx-button';
import { WdxInputTextModule } from '@wdx/shared/components/wdx-input-text';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WdxIconButtonModule } from '@wdx/shared/components/wdx-icon-button';
import { InfoCardData } from '../../../../models/info-card-data.model';
import { FormLookupSelectedItemsComponent } from '../lookup-selected-items/lookup-selected-items.component';
import { DynamicEntityHelperService } from '../../../../features/dynamic-pages/services/dynamic-entity-helper.service';
import { ColDef, GridOptions } from 'ag-grid-community';
import { SERVER_SIDE_DEFAULT_GRID_OPTIONS } from '@wdx/shared/components/ag-grid';
import { PartyToAvatarPipe } from '@wdx/clmi/utils/pipes';
import { NgClass } from '@angular/common';
import {
    ContactPointLookup,
    InfoCardService,
} from '../../../services/info-card/info-card.service';
import { FormLookupTableModalComponent } from '../lookup-table-modal/form-lookup-table-modal.component';
import { BehaviorSubject } from 'rxjs';
import { PushPipe } from '@ngrx/component';

@Component({
    selector: 'clmi-generic-advanced-lookup',
    templateUrl: './generic-advanced-lookup.component.html',
    styleUrls: ['./generic-advanced-lookup.component.scss'],
    imports: [
        WdxIsLoadingModule,
        WdxFormFieldModule,
        WdxInputGroupModule,
        MoleculeInfoCardModule,
        WdxIconModule,
        WdxInputTextModule,
        MoleculeInfoCardModule,
        WdxIconButtonModule,
        WdxButtonModule,
        FormLookupSelectedItemsComponent,
        NgClass,
        PushPipe,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DynamicEntityHelperService, PartyToAvatarPipe],
})
export class GenericAdvancedLookupComponent implements OnInit {
    @Input() entityTypes: SystemEntity[];
    @Output() blurred = new EventEmitter();
    @Output() valueChanges = new EventEmitter<LookupObject[]>();

    private modalService = inject(NgbModal);
    private destroyRef = inject(DestroyRef);
    private cdr = inject(ChangeDetectorRef);
    public infoCardService = inject(InfoCardService);
    public value: LookupObject[];
    public lookupCards$ = new BehaviorSubject<InfoCardData[]>([]);
    public columnDefs: ColDef[];

    public gridOptions: GridOptions = {
        ...SERVER_SIDE_DEFAULT_GRID_OPTIONS,
        pagination: false,
        suppressCellFocus: true,
        suppressHeaderFocus: true,
    };
    public formViewType: 'grid' | 'lookups';
    private layoutQueryExpressions: QueryExpression[];
    public gridQuery: QueryExpression[];
    public readonly WdxButtonStyle = WdxButtonStyle;
    public readonly WdxButtonSize = WdxButtonSize;
    public readonly UTILS_ICON_DELETE = UTILS_ICON_DELETE;
    public readonly UTILS_ICON_SEARCH = UTILS_ICON_SEARCH;
    public readonly FormElementStyle = FormElementStyle;

    private modalComponent: FormLookupTableModalComponent;

    ngOnInit(): void {
        if (this.value?.length) {
            this.setLookupCards(this.value);
        }
    }

    onOpen(): void {
        const instanceId = new RandomStringPipe().transform();
        const { componentInstance, closed, dismissed } = this.modalService.open(
            FormLookupTableModalComponent,
            {
                backdrop: 'static',
                backdropClass: `ngb-backdrop-${instanceId} fade-in modal-backdrop--ngb`,
                centered: true,
                windowClass: `ngb-window-${instanceId} fade-in modal-window--ngb`,
                modalDialogClass: 'modal-full',
            },
        );
        this.modalComponent = componentInstance;
        this.modalComponent.value =
            this.lookupCards$.value
                ?.filter((card) => Boolean(card.data))
                .map(({ data }) => data) || [];
        this.modalComponent.formInputData = {
            lookupSources: this.entityTypes.map((type) => ({ type })) as any,
            id: null,
        };
        this.modalComponent.isMulti = false;
        this.modalComponent.layoutQueryExpressions =
            this.layoutQueryExpressions;

        closed
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                ({
                    rows,
                }: {
                    rows?: { id: string; lookupType: LookupSourceType }[];
                }) => {
                    this.value = rows.flatMap((row) =>
                        this.infoCardService.convertQueryToLookupArray(
                            row.lookupType,
                            row,
                        ),
                    );

                    this.setLookupCards(rows);

                    this.cdr.markForCheck();
                    this.blurred.emit();
                    this.valueChanges.emit(this.value);
                },
            );
        dismissed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.modalComponent = undefined;
        });
    }

    deleteItem(item: { data: LookupFieldResult | ContactPointLookup }) {
        this.removeItem(item.data);
    }

    private removeItem(toDelete: { id?: string }) {
        if (!toDelete.id) {
            return;
        }
        this.value =
            this.value?.filter((item) => toDelete.id !== item.id) ?? [];

        this.lookupCards$.next(
            this.lookupCards$?.value.filter((item) => toDelete.id !== item.id) ??
                [],
        );

        this.valueChanges.emit(this.value);
    }

    setLookupCards(rows: LookupObject[]) {
        this.lookupCards$.next(
            this.infoCardService.lookupResultsAsInfoCardData(rows),
        );
    }
}
